import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useAgentForgeMessages,
  useAgentForgeStream,
  useBarKeyboardShortcut,
  useKulaTokenSync,
} from '../hooks';
import { useSessionsQuery } from '../api';
import { AgentForgeBar } from './bar';
import ThreadContainer from '../threadContainer';
import { HistoryPanel } from './history';
import { useAgentForgeStore } from '../stores';
import { useQueuedSendDispatcher } from '../hooks/useQueuedSendDispatcher';

// Single mount point for the whole feature. PrivateRoutes renders just this
// one component behind the feAgentForgeV1 flag — every surface (bar, thread,
// fullscreen, history) reads its own visibility from the Zustand store.
//
// Two background concerns are mounted here exactly once, matching legacy
// `/agentforge` exactly:
//
//   • WS subscriber (`useAgentForgeStream`). Single mount so each frame
//     is applied once. Drives live streaming + invalidates the messages
//     query on `stream_done`.
//
//   • Persisted-transcript fetcher (`useAgentForgeMessages`). Initial GET
//     on session open + re-fetch on stream-hook invalidation. Mirrors
//     into Zustand `messagesBySession`.
//
// No polling — legacy doesn't poll either. If the WS drops, the
// reconnect loop in `useAgentForgeWS` brings it back (1s → 30s backoff
// + tab-visibility resume); in-flight replies pause until then.
//
// NOTE: AgentForgeBar is restored in minimized-only mode (CORE-4669). It
// renders only when mode === 'minimized' so it won't show on every route —
// only after the user minimizes the overlay.
//
// HistoryPanel stays mounted globally — its visibility is gated by
// `historyOpen` in the store, which only the chat surface's UI ever
// toggles, so it never appears on unrelated routes.
export const AgentForge = () => {
  const activeSessionId = useAgentForgeStore((s) => s.activeSessionId);
  const openTabs = useAgentForgeStore((s) => s.openTabs);
  const openTab = useAgentForgeStore((s) => s.openTab);
  const floatingBridgePath = useAgentForgeStore((s) => s.floatingBridgePath);
  const setMode = useAgentForgeStore((s) => s.setMode);
  const { pathname } = useLocation();
  useBarKeyboardShortcut();
  const { reconnecting } = useAgentForgeStream(activeSessionId);
  useAgentForgeMessages(activeSessionId);
  useQueuedSendDispatcher();
  // Mirror socket health into the store so any chat surface can render the
  // "Reconnecting…" pill without opening a second socket (useAgentForgeWS
  // creates a connection per call — this umbrella is the single owner).
  const setWsReconnecting = useAgentForgeStore((s) => s.setWsReconnecting);
  useEffect(() => {
    setWsReconnecting(reconnecting);
  }, [reconnecting, setWsReconnecting]);
  // Keep the Kula token fresh (proactive: refresh when within 10 min of expiry).
  useKulaTokenSync();

  // Auto-close the context-bridge overlay. When the user clicks a result
  // (candidate / table row) from the inline agent view, the overlay floats
  // over the target page so the chat survives the jump. It should stay only
  // while they're on that page — once they navigate elsewhere, fully hide it
  // ('docked' renders neither the panel nor the minimized bar chip; the
  // session is kept, just nothing shows). `arrivedRef` guards the brief
  // pre-navigation tick where the bridge path is set but the router hasn't
  // landed on it yet, so we don't close early.
  const arrivedRef = useRef(false);
  useEffect(() => {
    if (!floatingBridgePath) {
      arrivedRef.current = false;
      return;
    }
    if (pathname.startsWith(floatingBridgePath)) {
      arrivedRef.current = true;
      return;
    }
    if (arrivedRef.current) {
      arrivedRef.current = false;
      setMode('docked');
    }
  }, [pathname, floatingBridgePath, setMode]);

  // Deep-link backfill: when the user lands on `/agent/c/<id>` (refresh
  // or pasted URL) and we don't have a tab for that session, look it up
  // in the sessions list and open one. Without this, the header falls
  // back to "New chat" with no timestamp.
  const sessionsQuery = useSessionsQuery({ enabled: !!activeSessionId });
  useEffect(() => {
    if (!activeSessionId) return;
    if (openTabs.some((t) => t.sessionId === activeSessionId)) return;
    const session = sessionsQuery.data?.find((s) => s.id === activeSessionId);
    if (!session) return;
    openTab({
      sessionId: session.id,
      title: session.title || 'Untitled chat',
      status: 'idle',
      createdAt: session.created_at,
    });
  }, [activeSessionId, openTabs, sessionsQuery.data, openTab]);

  return (
    <>
      <AgentForgeBar />
      <ThreadContainer />
      <HistoryPanel />
    </>
  );
};
